import React, { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ShieldAlert, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, decodeText } from "@/lib/api";
import { reasonLabel, ACTION_LABELS } from "@/lib/moderation";
import BanDialog from "@/components/admin/BanDialog";
import ReportActions from "@/components/admin/ReportActions";
import ReportConfirmDialog from "@/components/admin/ReportConfirmDialog";

const ago = (iso) => { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } };
const STATUS_STYLES = { pending: "bg-amber-50 text-amber-700 border-amber-200", escalated: "bg-red-50 text-red-600 border-red-200", resolved: "bg-emerald-50 text-emerald-700 border-emerald-200", dismissed: "bg-slate-100 text-slate-500 border-slate-200" };

export default function ReportItem({ report, onChanged }) {
  const [banOpen, setBanOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const pending = report.status === "pending" || report.status === "escalated";
  const isPrivate = report.kind === "private" || report.message_id?.startsWith("private:");

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === "delete") {
        await api.post(`/admin/public-messages/${report.message_id}/delete`, { reason: `Report: ${reasonLabel(report.reason)}`, report_id: report.id });
        toast.success("Message deleted for everyone");
      } else {
        await api.post(`/admin/reports/${report.id}/dismiss`, { reason: null });
        toast("Report dismissed");
      }
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false); setConfirm(null);
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:border-pink-100 hover:shadow-md sm:p-5" data-testid={`report-item-${report.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-pink-50 px-2.5 py-1 text-xs font-bold text-pink-600"><ShieldAlert className="h-3.5 w-3.5" />{isPrivate ? "Private message · " : ""}{reasonLabel(report.reason)}</span>
        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${STATUS_STYLES[report.status]}`} data-testid={`report-status-${report.id}`}>{report.status}</span>
        {!isPrivate && report.message_deleted && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">message removed</span>}
        {report.reported_user_banned && <span className="rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-600">author banned</span>}
        <span className="ml-auto flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" />{ago(report.created_at)}</span>
      </div>
      <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/80 p-3">
        <p className="text-xs font-semibold text-slate-500">@{report.reported_username} · {ago(report.message_created_at)}</p>
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800" data-testid={`report-message-text-${report.id}`}>{decodeText(report.message_text)}</p>
      </div>
      {report.details && <p className="mt-3 break-words text-sm text-slate-600"><span className="font-semibold text-slate-500">Details:</span> {decodeText(report.details)}</p>}
      <p className="mt-3 text-xs text-slate-400">Reported by <span className="font-semibold text-slate-600">@{report.reporter_username}</span>{!pending && report.reviewed_by_username && <> · {ACTION_LABELS[report.action_taken] || report.action_taken} by @{report.reviewed_by_username} {ago(report.reviewed_at)}</>}</p>
      {pending && <ReportActions report={report} busy={busy} onDismiss={() => setConfirm("dismiss")} onDelete={isPrivate ? undefined : () => setConfirm("delete")} onBan={() => setBanOpen(true)} onEscalated={onChanged} />}
      <BanDialog open={banOpen} onOpenChange={setBanOpen} username={report.reported_username} reportId={isPrivate ? undefined : report.id} onDone={onChanged} />
      <ReportConfirmDialog kind={confirm} onCancel={() => setConfirm(null)} onConfirm={() => act(confirm)} />
    </div>
  );
}
